import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import DateFnsUtils from '@date-io/date-fns';
import { formatISO9075 } from 'date-fns';
import { Grid, Fab, Icon, TextField, Switch, MenuItem, Button, RadioGroup, FormControlLabel, Radio, FormHelperText } from '@material-ui/core';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import NumberFormat from 'react-number-format';
import { uploadFileTypeUnsupported, uploadFileSizeLimit, uploadImageResolutionLimit } from 'redux/actions/general';
import { saveBannerSlideApi } from 'api';
import {fileToBase64} from '../../utils';


const MAX_BANNER_FILE_SIZE = 250 * 1024;

const EditBannerSlide = ({data, baseUrl, isOpen}) => {
  const dispatch = useDispatch();
  const [isNewImageLoad, setNewImageLoad] = useState(false);
  const phoneRegExp = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;
  const {
    image,
    title,
    alt_text,
    is_active,
    url_target,
    slide_sort_order,
    slide_startdate,
    slide_enddate,
    hosted_image,
    hosted_image_url,
    hosted_image_thumburl,
    url,
    group_id,
    slide_id
  } = data;

  const handleSaveSlideEdits = (values) => {
    const startDate = values.slide_startdate ? new Date(values.slide_startdate) : null;
    const endDate = values.slide_enddate ? new Date(values.slide_enddate) : null;

    if (values.onClickSwitcher === 'Phone' && values.phone !== '') {
      values.url = `tel:${values.phone}`;
      Reflect.deleteProperty(values, 'phone');
      Reflect.deleteProperty(values, 'onClickSwitcher');
    } else {
      Reflect.deleteProperty(values, 'phone');
      Reflect.deleteProperty(values, 'onClickSwitcher');
    }

    if (isNewImageLoad) {
      const slideData = {
        ...values,
        hosted_image: 0,
        slide_startdate: startDate ? formatISO9075(startDate) : null,
        slide_enddate: endDate ? formatISO9075(endDate) : null
      };
      saveBannerSlideApi({dispatch, slideData});
    } else {
      const slideData = {
        ...values,
        slide_startdate: startDate ? formatISO9075(startDate) : null,
        slide_enddate: endDate ? formatISO9075(endDate) : null,
        hosted_image: 1,
        hosted_image_url: values.hosted_image == 0
          ? `${baseUrl}/media/flexslider/${values.image}`
          : values.hosted_image_url,
        hosted_image_thumburl: values.hosted_image == 0
          ? `${baseUrl}/media/flexslider/${values.image}`
          : values.hosted_image_url
      };
      saveBannerSlideApi({dispatch, slideData});
    }

    isOpen(false);
  };

  const handleUploadBanner = (event, setValue) => {
    const {files} = event.target;
    const fileName = files[0].name;
    const fileTypeDotIndexPosition = fileName.lastIndexOf('.') + 1;
    const slicedFileTypeFromFilePath = fileName.slice(fileTypeDotIndexPosition);
    const acceptableFormats = 'png|jpeg|gif|jpg';
    const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');
    const imageSizeValues = {
      height: 0,
      width: 0
    };
    const reader = new FileReader();

    reader.readAsDataURL(files[0]);
    reader.onload = (e) => {
      const image = new Image();

      image.src = e.target.result;
      image.onload = async () => {
        const {height, width} = image;

        imageSizeValues.height = height;
        imageSizeValues.width = width;

        if (!hasImageFile.test(files[0].type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
          dispatch(uploadFileTypeUnsupported());
        } else if (files[0].size > MAX_BANNER_FILE_SIZE) {
          dispatch(uploadFileSizeLimit('250kB'));
        } else if (imageSizeValues.width !== 1660 || imageSizeValues.height !== 500) {
          dispatch(uploadImageResolutionLimit('1660 x 500px'));
        } else {
          const file = await fileToBase64(files[0]);
          setValue('image',  btoa(file));
          setValue('hosted_image',  '0');
          setNewImageLoad(true);
        }
      };
    };
  };

  return (
    <>
      <Formik
        initialValues={{
          image,
          title,
          alt_text,
          is_active,
          url_target: url_target || '_self',
          slide_sort_order,
          slide_startdate: slide_startdate ? new Date(slide_startdate.replace(/-/g, '/')) : null,
          slide_enddate: slide_enddate ? new Date(slide_enddate.replace(/-/g, '/')) : null,
          url: url?.includes('tel:') ? '' : url,
          hosted_image,
          hosted_image_url,
          hosted_image_thumburl,
          group_id,
          slide_id,
          phone: url?.includes('tel:') ? url.replace('tel:', '') : '',
          onClickSwitcher: url?.includes('tel:') ? 'Phone' : 'URL',
        }}
        onSubmit={(values) => handleSaveSlideEdits(values)}
        enableReinitialize
        validationSchema={
          yup.object({
            title: yup
              .string()
              .required('Field is required'),
            slide_sort_order: yup
              .number('Only numbers allowed')
              .min('0'),
            alt_text: yup
              .string()
              .required('Field is required'),
            phone: yup
              .string()
              .matches(phoneRegExp, 'Phone number is not valid'),
            slide_startdate: yup
              .date()
              .nullable()
              .test('slide_startdate', 'Start Date can\'t be more than End Date', function () {
                const { slide_startdate, slide_enddate } = this.parent;
                return !(slide_enddate && slide_startdate && slide_enddate < slide_startdate);
              })
              .test('slide_startdate', 'Can\'t enter past dates', function () {
                const { slide_startdate } = this.parent;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return !(slide_startdate && slide_startdate < today);
              })
              .typeError('Invalid date'),
            slide_enddate: yup
              .date()
              .nullable()
              .test('slide_enddate', 'End Date can\'t be less than Start Date', function () {
                const { slide_startdate, slide_enddate } = this.parent;
                return !(slide_enddate && slide_startdate && slide_enddate < slide_startdate);
              })
              .test('slide_enddate', 'Can\'t enter past dates', function () {
                const { slide_enddate } = this.parent;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return !(slide_enddate && slide_enddate < today);
              })
              .typeError('Invalid date'),
          })
        }
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleSubmit,
          setFieldValue,
          handleChange
        }) => (
          <form onSubmit={handleSubmit} className="p-8">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {(/^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)?$/.test(values.image) && !!values.image.length)
                  ? <img src={`data:image/png;base64,${values.image}`} alt={values.alt_text} className="w-full" />
                  :
                  <img
                    src={values.hosted_image == 1
                      ? values.hosted_image_url
                      : `${baseUrl}/media/flexslider/${values.image}`
                    }
                    alt={values.alt_text}
                    className="w-full"
                  />
                }
              </Grid>
              <Grid item xs={12}>
                <label htmlFor="upload-image">
                  <Fab
                    component="span"
                    variant="extended"
                    className="border-radius-30 capitalize bg-white text-13 h-30px text-primary font-bold border-1-primary box-shadow-none w-full"
                  >
                    <Icon className="pr-8" color="primary">cloud_upload</Icon>
                    <span>Upload New Image</span>
                  </Fab>
                </label>
                <input
                  className="hidden"
                  onChange={(event) => handleUploadBanner(event, setFieldValue)}
                  id="upload-image"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                />
                <FormHelperText className="mt-2">
                  <div>Files supported: jpeg, jpg, png (jpeg, jpg preferred). File size should be less than 250kB.</div>
                  <div>Image resolution should be exactly 1660x500px.</div>
                </FormHelperText>
              </Grid>
            </Grid>
            <Grid container spacing={3} className="mt-4 flex items-center">
              <Grid item xs={12} sm={4} md={3}>Title: <span className="text-error">*</span></Grid>
              <Grid item xs={12} sm={8} md={9}>
                <TextField
                  fullWidth
                  required
                  name="alt_text"
                  value={values.alt_text}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.alt_text && Boolean(errors.alt_text)}
                  helperText={touched.alt_text && errors.alt_text}
                />
              </Grid>
            </Grid>
            <Grid container spacing={3} className="mt-4 flex items-center">
              <Grid item xs={12} sm={4} md={3}>Enable:</Grid>
              <Grid item xs={12} sm={8} md={9}>
                <Switch
                  color="primary"
                  name="is_active"
                  checked={values.is_active == '1'}
                  onChange={(event) => setFieldValue('is_active', event.target.checked ? '1' : '0')}
                />
              </Grid>
            </Grid>
            <Grid container spacing={3} className="mt-4 flex items-center">
              <Grid item xs={12} sm={4} md={3}>Sort Order:</Grid>
              <Grid item xs={12} sm={8} md={9}>
                <TextField
                  fullWidth
                  name="slide_sort_order"
                  value={values.slide_sort_order}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.slide_sort_order && Boolean(errors.slide_sort_order)}
                  helperText={touched.slide_sort_order && errors.slide_sort_order}
                />
              </Grid>
            </Grid>
            <Grid container spacing={3} className="mt-4 flex items-center">
              <Grid item xs={12} sm={4} md={3}>OnClick URL/Phone:</Grid>
              <Grid item xs={12} sm={8} md={9}>
                <RadioGroup
                  aria-label="OnClick URL/Phone"
                  name="onClickSwitcher"
                  value={values.onClickSwitcher}
                  onChange={(event) => {
                    setFieldValue('phone', '');
                    setFieldValue('url', '');
                    handleChange(event);
                  }}
                  row
                >
                  <FormControlLabel value="URL" control={<Radio color="primary" />} label="URL" />
                  <FormControlLabel value="Phone" control={<Radio color="primary" />} label="Phone" />
                </RadioGroup>
              </Grid>
            </Grid>
            {values.onClickSwitcher === 'Phone' ?
              <Grid container spacing={3} className="mt-4 flex items-center">
                <Grid item xs={12} sm={4} md={3}>OnClick Phone:</Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <NumberFormat
                    customInput={TextField}
                    format="+1##########"
                    mask="_"
                    className="w-full"
                    name="phone"
                    value={values.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                  />
                </Grid>
              </Grid>
              :
              <Grid container spacing={3} className="mt-4 flex items-center">
                <Grid item xs={12} sm={4} md={3}>OnClick URL:</Grid>
                <Grid item xs={12} sm={8} md={9}>
                  <TextField
                    fullWidth
                    name="url"
                    value={values.url}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.url && Boolean(errors.url)}
                    helperText={touched.url && errors.url}
                  />
                </Grid>
              </Grid>
            }
            <Grid container spacing={3} className="mt-4 flex items-center">
              <Grid item xs={12} sm={4} md={3}>OnClick Target:</Grid>
              <Grid item xs={12} sm={8} md={9}>
                <TextField
                  select
                  name="url_target"
                  value={values.url_target}
                  onChange={(event) => setFieldValue('url_target', event.target.value)}
                >
                  <MenuItem value="_self">Same Window / Tab</MenuItem>
                  <MenuItem value="_blank">New Window / Tab</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <Grid container spacing={3} className="flex items-center">
              <Grid item xs={12} sm={4} md={3}>Slide Start Date:</Grid>
              <Grid item xs={12} sm={8} md={9}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDateTimePicker
                    format="MM/dd/yyyy, hh:mm a"
                    margin="normal"
                    name="slide_startdate"
                    disablePast
                    value={values.slide_startdate}
                    onChange={(value) => setFieldValue('slide_startdate', value)}
                    error={Boolean(errors.slide_startdate)}
                    helperText={errors.slide_startdate ? errors.slide_startdate : 'Leave empty to always show this slide'}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
            </Grid>
            <Grid container spacing={3} className="flex items-center">
              <Grid item xs={12} sm={4} md={3}>Slide End Date:</Grid>
              <Grid item xs={12} sm={8} md={9}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDateTimePicker
                    format="MM/dd/yyyy, hh:mm a"
                    margin="normal"
                    name="slide_enddate"
                    disablePast
                    value={values.slide_enddate}
                    onChange={(value) => setFieldValue('slide_enddate', value)}
                    error={Boolean(errors.slide_enddate)}
                    helperText={errors.slide_enddate ? errors.slide_enddate : 'Leave empty to always show this slide'}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
            </Grid>
            <div className="mt-8 flex items-center justify-between">
              <Button
                type="submit"
                color="primary"
                variant="contained"
                className="capitalize"
              >
                Save Edits
              </Button>
              <Button
                variant="contained"
                className="capitalize"
                onClick={() => isOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export default EditBannerSlide;
